import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import Tag from "@/components/base/Tag";
import NewsCard from "@/components/feature/NewsCard";
import { characters } from "@/mocks/characters";
import { episodes } from "@/mocks/episodes";
import { news } from "@/mocks/news";
import useDocumentMeta from "@/hooks/useDocumentMeta";

export default function CharacterDetail() {
  const { id } = useParams();
  const character = characters.find((c) => c.id === id);

  useDocumentMeta(
    character
      ? {
          title: `${character.name}角色介绍 - 美国禁忌4 人物档案`,
          description: character.bio,
        }
      : { title: "角色详情 - 美国禁忌4", description: "美国禁忌4角色档案详情。" }
  );

  if (!character) {
    return (
      <div className="px-4 md:px-10 py-16 text-center">
        <h1 className="font-heading text-2xl text-foreground-950">未找到该角色</h1>
        <p className="mt-3 text-sm text-foreground-500">你访问的角色可能已被移除或链接有误。</p>
        <Link
          to="/characters"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-label text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" />
          返回角色列表
        </Link>
      </div>
    );
  }

  const appearances = character.episodes
    .map((eid) => episodes.find((e) => e.id === eid))
    .filter((e): e is (typeof episodes)[number] => Boolean(e));

  const relationChars = character.relations
    .map((r) => ({ ...r, char: characters.find((c) => c.id === r.charId) }))
    .filter((r) => Boolean(r.char));

  const relatedNews = news
    .map((item) => ({
      item,
      score:
        (item.title.includes(character.name) || item.summary.includes(character.name) ? 3 : 0) +
        (item.tags.includes("角色解读") ? 1 : 0),
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((r) => r.item);

  return (
    <div className="pb-6">
      {/* Header */}
      <div className="relative w-full overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr]">
          <div className="relative h-[320px] md:h-[400px]">
            <img
              src={character.avatar}
              alt={`${character.name} 角色形象`}
              title={`${character.name} - 美国禁忌4 角色档案`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/20 to-transparent md:bg-gradient-to-r md:from-transparent md:via-transparent md:to-background-50" />
          </div>
          <div className="flex flex-col justify-end px-4 md:px-10 py-6 md:py-10">
            <Tag tone={character.type === "反派" ? "accent" : "primary"}>{character.type}</Tag>
            <h1 className="mt-3 font-heading text-3xl md:text-5xl font-semibold tracking-wide text-foreground-950">
              {character.name}
            </h1>
            <p className="font-label text-xs tracking-[0.28em] text-accent-400 uppercase mt-2">{character.enName}</p>
            <p className="mt-4 text-[15px] text-foreground-700 leading-relaxed max-w-2xl">{character.bio}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {character.seasons.map((s) => (
                <Tag key={s} tone="secondary">
                  第 {s} 季登场
                </Tag>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-10">
        <div className="py-5 border-b border-background-200">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "角色介绍", to: "/characters" },
              { label: character.name },
            ]}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.7fr_1fr] gap-8 md:gap-10 mt-8">
          <div>
            <section>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 tracking-wide mb-4">角色背景故事</h2>
              <div className="space-y-4">
                {character.background.map((p, i) => (
                  <p key={i} className="text-[15px] leading-8 text-foreground-700">
                    {p}
                  </p>
                ))}
              </div>
            </section>

            <section className="mt-10">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 tracking-wide mb-4">人物关系网络</h2>
              <ul className="space-y-3">
                {relationChars.map((r) => (
                  <li key={r.name}>
                    <Link
                      to={`/characters/${r.charId}`}
                      className="group flex items-center gap-4 rounded-lg bg-background-100 border border-background-200 hover:border-accent-500/40 transition-colors p-3 md:p-4 cursor-pointer"
                    >
                      <div className="w-14 h-14 shrink-0 rounded-md overflow-hidden">
                        <img
                          src={r.char!.avatar}
                          alt={`${r.name} 角色形象`}
                          title={`${r.name} - 美国禁忌4 角色档案`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="font-heading text-sm text-foreground-950 group-hover:text-accent-400 transition-colors">
                          {r.name}
                        </p>
                        <p className="mt-1 text-[13px] text-foreground-500">{r.relation}</p>
                      </div>
                      <i className="ri-arrow-right-line text-foreground-600 ml-auto group-hover:text-accent-400 transition-colors" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>

            <section className="mt-10">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 tracking-wide mb-4">
                登场剧集章节
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {appearances.map((ep) => (
                  <Link
                    key={ep.id}
                    to={`/episodes/${ep.id}`}
                    className="group flex items-center gap-3 rounded-lg bg-background-100 border border-background-200 hover:border-accent-500/40 transition-colors p-3 cursor-pointer"
                  >
                    <span className="font-label text-xs text-accent-400 w-14 shrink-0">
                      S{ep.season}·E{ep.episodeNumber}
                    </span>
                    <span className="text-sm text-foreground-900 truncate group-hover:text-accent-400 transition-colors">
                      {ep.title}
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg bg-background-100 border border-background-200 p-5">
              <h2 className="font-heading text-base text-foreground-950 tracking-wide mb-4">角色属性</h2>
              <dl className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">饰演演员</dt>
                  <dd className="text-foreground-900">{character.actor}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">角色类型</dt>
                  <dd className="text-foreground-900">{character.type}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">登场季数</dt>
                  <dd className="text-foreground-900">第 {character.seasons.join(" / ")} 季</dd>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <dt className="text-foreground-500 shrink-0">角色定位</dt>
                  <dd className="text-foreground-900 text-right">{character.role}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">登场集数</dt>
                  <dd className="text-foreground-900">{appearances.length} 集</dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>

        <section className="mt-12 md:mt-16">
          <div className="flex items-center gap-2 mb-5">
            <span className="w-6 h-[2px] bg-accent-500 inline-block" />
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 tracking-wide">相关资讯与专题</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
            {relatedNews.map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}